import { List, Map } from "immutable";
import { z } from "zod";

import {
	RunnableQuery,
	type RunnableQueryState,
	hydrateRunnableQueryState,
	rawRunnableQueryStateSchema,
} from "../RunnableQuery";
import { type ZSet, hydrateZSet, rawZSetSchema } from "../ZSet";
import { querySpecSchema } from "../operatorsV2/QuerySpec";
import {
	type Action,
	type MutationAction,
	type QueryRegistrationAction,
	rawActionSchema,
} from "./Action";
import { hydrateTableMutation } from "./TableMutation";

export interface QueryEvaluatorState {
	currentEpoch: number;
	tables: Map<string, ZSet<unknown>>;
	querySpecs: Map<string, RunnableQuery>;
	queryStates: Map<string, RunnableQueryState>;
	queryResults: Map<string, ZSet<unknown>>;
	actionLog: List<Action>;
}

//
// Zod schemas for JSON-serialized QueryEvaluatorState.
//

export const emptyQueryEvaluatorState = (): QueryEvaluatorState => ({
	currentEpoch: 0,
	tables: Map(),
	querySpecs: Map(),
	queryStates: Map(),
	queryResults: Map(),
	actionLog: List(),
});

export const hydrateQueryEvaluatorState = (state: RawQueryEvaluatorState): QueryEvaluatorState => {
	const { currentEpoch } = state;
	const tables = recordToMap(state.tables, hydrateZSet);
	const querySpecs = recordToMap(state.querySpecs, RunnableQuery.fromSpec);
	const queryStates = recordToMap(state.queryStates, hydrateRunnableQueryState);
	const queryResults = recordToMap(state.queryResults, hydrateZSet);
	const actionLog = List(
		state.actions.map((action) => {
			switch (action.type) {
				case "mutation":
					return {
						type: "mutation",
						intentId: action.intentId,
						table: action.table,
						desiredApplyEpoch: action.desiredApplyEpoch,
						mutation: hydrateTableMutation(action.mutation),
					} satisfies MutationAction;
				case "queryRegistration":
					return action satisfies QueryRegistrationAction;
			}
		})
	);
	return {
		currentEpoch,
		tables,
		querySpecs,
		queryStates,
		queryResults,
		actionLog,
	};
};

function recordToMap<T, U>(record: Record<string, T>, f: (value: T) => U): Map<string, U> {
	return Map(Object.entries(record).map(([key, value]) => [key, f(value)]));
}

export const rawQueryEvaluatorStateSchema = z.object({
	currentEpoch: z.number(),
	tables: z.record(z.string(), rawZSetSchema),
	querySpecs: z.record(z.string(), querySpecSchema),
	queryStates: z.record(z.string(), rawRunnableQueryStateSchema),
	queryResults: z.record(z.string(), rawZSetSchema),
	actions: z.array(rawActionSchema),
});
export type RawQueryEvaluatorState = z.infer<typeof rawQueryEvaluatorStateSchema>;
